import React, {
  useEffect,
  useState,
  useCallback,
  useContext,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import { connect, useDispatch } from "react-redux";
import TooltipIcon from "../common/TooltipIcon";
import {
  Card,
  Tabs,
  Typography,
  Row,
  Col,
  Tag,
  Spin,
  Tooltip,
  Alert,
  Space,
  Divider,
  Button,
  message,
} from "antd";
import {
  TeamOutlined,
  ExclamationCircleOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import GroupDetails from "./GroupDetails";
import { types } from "@authzsvc/api-lib";
import {
  getGroupsIOwn,
  selectGroupById,
  getGroupModerators,
  getGroupReaders,
  getIdentityById,
} from "../../actions/api";
import {
  isApiCallLoading,
  userTokenHasRole,
  userTokenHasGroupUserRole,
} from "../../selectors";
import GroupEmailProperties from "./GroupEmailProperties/GroupEmailProperties";
import GroupCriteria from "./GroupCriteria/GroupCriteria";
import GroupMembers from "./GroupMembers";
import EntityAuditLogs from "./EntityAuditLogs";
import GroupTreeView from "./GroupTreeView";
import FlattenedGroupMembership from "./FlattenedGroupMembership";
import GroupSyncTypePanel from "./syncTypes/GroupSyncTypePanel";
import GroupRoles from "./GroupRoles";
import { TitleContext } from "../../hooks";
import SubscriptionButton from "./SubscriptionButton";
import MailingAddressSelector from "./MailingAddressSelector";
import { adminRoleIdentifier, groupsUserRoleIdentifier } from "../../config";
import { GroupValidInternalStates } from "../../constants/enums";
import GroupModeratorsAndReaders from "./GroupModeratorsAndReaders/GroupModeratorsAndReaders";
import { types as at, errorAction } from "@authzsvc/api-lib";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  UserCategory,
  userCanRead,
  userCanWrite,
  resolveUserCategory,
  GroupTabs,
} from "./GroupUtils";
import { showErrorNotification } from "../../util";

// Mapping of tab names to their keys
const TAB_NAME_TO_KEY = {
  details: "0",
  members: "1",
  "moderators-and-readers": "2",
  "group-role-assignments": "3",
  "group-tree-view": "4",
  synchronization: "5",
  audit: "6",
  criteria: "7",
  "email-properties": "8",
  review: "9",
};

// Reverse mapping of keys to tab names
const TAB_KEY_TO_NAME = Object.fromEntries(
  Object.entries(TAB_NAME_TO_KEY).map(([name, key]) => [key, name])
);

const GroupDetailsTabs = (props) => {
  const {
    // redux data
    loading,
    selectedGroup,
    isAdmin,
    isExternalUser,
    identity,
    groupMemberships,
    groupMembershipDirect,
    // redux actions
    selectGroupById,
    getGroupsIOwn,
  } = props;

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const { setAction } = useContext(TitleContext);

  const [activeKey, setActiveKey] = useState("0");
  const [refreshMembers, setRefreshMembers] = useState(false);
  const [moderators, setModerators] = useState([]);
  const [readers, setReaders] = useState([]);
  const [manager, setManager] = useState({ upn: "", displayName: "" });
  const [membershipDataLoaded, setMembershipDataLoaded] = useState(false);
  const { id } = useParams();

  const groupHasManager = Boolean(selectedGroup?.managerId);
  const managedButMembershipModifiable =
    selectedGroup?.managedButMembershipModifiable;
  const managedButAllModifiable = selectedGroup?.managedButAllModifiable;

  const getManagePermissions = () => {
    if (!groupHasManager) {
      return {
        full: true,
        partial: true,
      };
    }

    if (managedButAllModifiable) {
      return {
        full: true,
        partial: true,
      };
    }

    if (managedButMembershipModifiable) {
      return {
        full: false,
        partial: true,
      };
    }

    return {
      full: false,
      partial: false,
    };
  };

  const managePermissions = getManagePermissions();

  // Extract tab from URL path (format: /group/:id/:tabName)
  const getTabKeyFromPath = useCallback(() => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    // Expected format: ["group", "groupId", "tabName"]
    if (pathSegments.length >= 3) {
      const tabName = pathSegments[2];
      return TAB_NAME_TO_KEY[tabName] || "0";
    }
    return "0";
  }, [location.pathname]);

  // Handle tab change and update URL
  const handleTabChange = (key) => {
    setActiveKey(key);
    const tabName = TAB_KEY_TO_NAME[key] || "details";
    navigate(`/group/${id}/${tabName}`);
  };

  const handleSelectGroupById = useCallback(
    async (id) => {
      const result = await selectGroupById(id);
      if (result.type === errorAction(at.GET_GROUP_BY_ID)) {
        navigate(`/`);

        showErrorNotification(
          result?.payload?.title || "Failed to find group",
          result?.payload?.data?.message || "Sorry, something went wrong"
        );
      }
      getGroupsIOwn();
    },
    [selectGroupById, navigate, getGroupsIOwn]
  );

  const fetchManagerInfo = async () => {
    if (selectedGroup?.managerId) {
      const result = await dispatch(
        getIdentityById(selectedGroup?.managerId, {})
      );
      setManager({
        upn: result?.payload?.data?.upn || "",
        displayName: result?.payload?.data?.displayName || "",
      });
    }
  };

  const getModerators = useCallback(async () => {
    const result = await dispatch(getGroupModerators(id));
    const isErrorAction = result.type === errorAction(at.GET_GROUP_MODERATORS);
    if (!isErrorAction) {
      setModerators(result.payload.data);
    }
  }, [dispatch, id]);

  const getReaders = useCallback(async () => {
    const result = await dispatch(getGroupReaders(id));
    const isErrorAction = result.type === errorAction(at.GET_GROUP_READERS);
    if (!isErrorAction) {
      setReaders(result.payload.data);
    }
  }, [dispatch, id]);

  const { groupMembershipInfo, userCategory } = useMemo(() => {
    const isModerator = moderators.some((m) =>
      groupMemberships.some((g) => g.id === m.id)
    );
    const isReader = readers.some((r) =>
      groupMemberships.some((g) => g.id === r.id)
    );
    const isGroupAdmin = groupMemberships.some(
      (g) => g.id === selectedGroup?.administratorsId
    );
    const isOwner = selectedGroup?.ownerId === identity?.id;

    const roles = [];
    if (isAdmin) roles.push(UserCategory.GLOBAL_ADMIN);
    if (isOwner) roles.push(UserCategory.OWNER);
    if (isGroupAdmin) roles.push(UserCategory.ADMIN);
    if (isModerator) roles.push(UserCategory.MODERATOR);
    if (isReader) roles.push(UserCategory.READER);

    const userCategory = resolveUserCategory(roles);

    return {
      groupMembershipInfo: {
        owner: isOwner,
        admin: isGroupAdmin,
        member: groupMemberships.some((i) => i.id === selectedGroup?.id),
        memberDirect: groupMembershipDirect.some(
          (i) => i.id === selectedGroup?.id
        ),
        moderator: isModerator,
        reader: isReader,
      },
      userCategory,
    };
  }, [
    moderators,
    readers,
    groupMemberships,
    groupMembershipDirect,
    selectedGroup,
    identity,
    isAdmin,
  ]);

  const isReplicaGroup =
    selectedGroup.syncType === "SlaveWithPlaceholders" ||
    selectedGroup.syncType === "Slave";

  const disableByInternalState = () => {
    if (
      Object.values(GroupValidInternalStates).includes(
        selectedGroup.internalState
      )
    ) {
      return false;
    }
    return true;
  };

  const handleRefreshMembers = () => {
    setRefreshMembers((prev) => !prev);
  };

  const getPanes = () => {
    let panes = [
      {
        key: "0",
        label: "Details",
        tabKey: "details-tab",
        children: (
          <div role="tabpanel" aria-labelledby="details-tab" tabIndex={0}>
            <GroupDetails
              canManageGroup={userCanWrite(
                userCategory,
                GroupTabs.DETAILS,
                !managePermissions.partial
              )}
              isDisabledByInternalState={
                disableByInternalState() ||
                !userCanRead(userCategory, GroupTabs.DETAILS)
              }
              isReader={groupMembershipInfo.reader}
              userCategory={userCategory}
              managedButMembershipModifiable={
                managedButAllModifiable ? false : managedButMembershipModifiable
              }
              isAdmin={isAdmin}
              {...props}
            />
          </div>
        ),
      },
      userCanRead(userCategory, GroupTabs.MEMBERS) && {
        key: "1",
        label: "Members",
        tabKey: "members-tab",
        children: (
          <div role="tabpanel" aria-labelledby="members-tab" tabIndex={1}>
            <GroupMembers
              canManageGroup={
                userCanWrite(
                  userCategory,
                  GroupTabs.MEMBERS,
                  !managePermissions.partial
                ) && !disableByInternalState()
              }
              refreshMembers={refreshMembers}
              matchId={id}
              {...props}
            />
          </div>
        ),
      },
      userCanRead(userCategory, GroupTabs.MODERATORS_AND_READERS) && {
        key: "2",
        label: "Moderators and Readers",
        tabKey: "moderators-readers-tab",
        children: (
          <div
            role="tabpanel"
            aria-labelledby="moderators-readers-tab"
            tabIndex={2}
          >
            <GroupModeratorsAndReaders
              canManageGroup={userCanWrite(
                userCategory,
                GroupTabs.MODERATORS_AND_READERS,
                !managePermissions.full
              )}
              refreshMembers={refreshMembers}
              isReplicaGroup={isReplicaGroup}
              {...props}
            />
          </div>
        ),
      },

      userCanRead(userCategory, GroupTabs.GROUP_ROLE_ASSIGNMENTS) && {
        key: "3",
        label: "Group role assignments",
        tabKey: "group-role-assignments-tab",
        children: (
          <div
            role="tabpanel"
            aria-labelledby="group-role-assignments-tab"
            tabIndex={3}
          >
            <GroupRoles {...props} />
          </div>
        ),
      },
      userCanRead(userCategory, GroupTabs.GROUP_TREE_VIEW) && {
        key: "4",
        label: "Group tree view",
        tabKey: "group-tree-view-tab",
        children: (
          <div
            role="tabpanel"
            aria-labelledby="group-tree-view-tab"
            tabIndex={4}
          >
            <GroupTreeView {...props} />,
          </div>
        ),
      },
      userCanRead(userCategory, GroupTabs.SYNCHRONIZATION) && {
        key: "5",
        label: "Synchronization",
        tabKey: "synchronization-tab",
        children: (
          <div
            role="tabpanel"
            aria-labelledby="synchronization-tab"
            tabIndex={5}
          >
            <GroupSyncTypePanel
              canManageGroup={userCanWrite(
                userCategory,
                GroupTabs.SYNCHRONIZATION,
                !managePermissions.full
              )}
              {...props}
            />
          </div>
        ),
      },
      selectedGroup.dynamic &&
        userCanRead(userCategory, GroupTabs.CRITERIA) && {
          key: "7",
          label: "Criteria",
          tabKey: "criteria-tab",
          children: (
            <div role="tabpanel" aria-labelledby="criteria-tab" tabIndex={7}>
              <GroupCriteria
                selectedGroup={selectedGroup}
                canManageGroup={
                  userCanWrite(
                    userCategory,
                    GroupTabs.CRITERIA,
                    !managePermissions.partial
                  ) && !disableByInternalState()
                }
                isReplicaGroup={isReplicaGroup}
              />
            </div>
          ),
        },
      userCanRead(userCategory, GroupTabs.EMAIL_PROPERTIES) && {
        key: "8",
        label: "Email Properties",
        tabKey: "email-properties-tab",
        children: (
          <div
            role="tabpanel"
            aria-labelledby="email-properties-tab"
            tabIndex={8}
          >
            <GroupEmailProperties
              selectedGroup={selectedGroup}
              canManageGroup={
                userCanWrite(
                  userCategory,
                  GroupTabs.EMAIL_PROPERTIES,
                  groupHasManager && !managedButAllModifiable
                ) && !disableByInternalState()
              }
              isReplicaGroup={isReplicaGroup}
            />
          </div>
        ),
      },
      userCanRead(userCategory, GroupTabs.AUDIT) && {
        key: "6",
        label: "Audit logs",
        tabKey: "audit-logs-tab",
        children: (
          <div role="tabpanel" aria-labelledby="audit-logs-tab" tabIndex={6}>
            <EntityAuditLogs {...props} selectedGroup={selectedGroup} />,
          </div>
        ),
      },
      userCanRead(userCategory, GroupTabs.GROUP_FLATTENED_MEMBERS) && {
        key: "9",
        label: (
          <span>
            Review <TooltipIcon size={16} title="Beta Release." />
          </span>
        ),
        tabKey: "review",
        children: (
          <div role="tabpanel" aria-labelledby="review-tab" tabIndex={9}>
            <FlattenedGroupMembership {...props} />,
          </div>
        ),
      },
    ].filter(Boolean);

    if (isExternalUser && !isAdmin) {
      panes.splice(2);
      return panes;
    }

    if (selectedGroup && selectedGroup.syncType === "SyncError") {
      panes.splice(panes.length - 1, 1);
    }

    return panes;
  };

  const getUserTags = () => {
    const tags = [];

    if (groupMembershipInfo.owner) {
      tags.push(
        <Tooltip key="owner" title="You are the owner of this group">
          <Tag aria-label="You are the owner of this group" color="red">
            Owner
          </Tag>
        </Tooltip>
      );
    }

    if (groupMembershipInfo.admin) {
      tags.push(
        <Tooltip key="admin" title="You are an admin of this group">
          <Tag aria-label="You are an admin of this group" color="blue">
            Admin
          </Tag>
        </Tooltip>
      );
    }

    if (groupMembershipInfo.memberDirect) {
      tags.push(
        <Tooltip key="member" title="You are a member of this group">
          <Tag aria-label="You are a member of this group" color="green">
            Member
          </Tag>
        </Tooltip>
      );
    }

    if (groupMembershipInfo.moderator) {
      tags.push(
        <Tooltip key="moderator" title="You are a moderator of this group">
          <Tag aria-label="You are a moderator of this group" color="blue">
            Moderator
          </Tag>
        </Tooltip>
      );
    }

    if (groupMembershipInfo.reader) {
      tags.push(
        <Tooltip key="reader" title="You are a reader of this group">
          <Tag aria-label="You are a reader of this group" color="blue">
            Reader
          </Tag>
        </Tooltip>
      );
    }

    return tags;
  };

  useEffect(() => {
    handleSelectGroupById(id);
    setActiveKey("0");
  }, [selectGroupById, id, getGroupsIOwn, handleSelectGroupById]);

  useEffect(() => {
    if (selectedGroup) {
      setAction(`ID: ${selectedGroup.groupIdentifier}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup, setAction]);

  useEffect(() => {
    fetchManagerInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup.managerId]);

  useEffect(() => {
    const fetchAll = async () => {
      setMembershipDataLoaded(false);

      setModerators([]);
      setReaders([]);

      await handleSelectGroupById(id);
      await Promise.all([getModerators(), getReaders()]);
      setMembershipDataLoaded(true);
    };
    fetchAll();
  }, [id, handleSelectGroupById, getModerators, getReaders]);

  // Sync activeKey with URL path
  useEffect(() => {
    const tabKey = getTabKeyFromPath();
    setActiveKey(tabKey);
  }, [location.pathname, getTabKeyFromPath]);

  const getManagerMessage = () => {
    if (managedButAllModifiable)
      return `This group is managed by "${manager.displayName} (${manager.upn})". All modifications are possible but might be overwritten.`;

    const message = managedButMembershipModifiable
      ? `This group is managed by "${manager.displayName} (${manager.upn})". Some options in details , membership, criteria are available for modification.`
      : `This group is managed by "${manager.displayName} (${manager.upn})" and cannot be modified.`;

    return message;
  };

  return (
    <>
      {loading || selectedGroup?.id === undefined || !membershipDataLoaded ? (
        <Card style={{ textAlign: "center" }}>
          <Spin size="large" aria-label="Loading group information" />
        </Card>
      ) : (
        <>
          <Row align="middle">
            <Col flex="32px" style={{ marginRight: 16 }}>
              <TeamOutlined
                style={{ fontSize: 32, color: "#bfbfbf" }}
                aria-hidden="true"
              />
            </Col>
            <Col flex="auto">
              <Typography.Title
                level={4}
                style={{ marginTop: 0 }}
                id="group-identifier-heading"
              >
                <Typography.Text
                  style={{ color: "red", fontSize: "inherit", marginRight: 8 }}
                >
                  {selectedGroup.disabled ? "[Blocked] " : ""}
                </Typography.Text>
                <Typography.Text
                  type="secondary"
                  style={{ marginRight: 8, fontSize: "inherit" }}
                >
                  Group:
                </Typography.Text>
                {selectedGroup.groupIdentifier}
                <Tooltip title="Copy group link">
                  <Button
                    aria-label="Copy group link"
                    type="text"
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => {
                      const deterministicGroupUrl = `${
                        window.location.origin
                      }/group/${encodeURIComponent(
                        selectedGroup.groupIdentifier
                      )}`;

                      navigator.clipboard
                        .writeText(deterministicGroupUrl)
                        .then(() => {
                          message.success("Group link copied to clipboard!");
                        })
                        .catch(() => {
                          message.error("Unable to copy group link.");
                        });
                    }}
                  />
                </Tooltip>
              </Typography.Title>
              {getUserTags()}
            </Col>
            <Col
              flex="none"
              style={{ textAlign: "right", gap: 8, display: "flex" }}
            >
              {selectedGroup.selfSubscriptionType !== "Closed" &&
                !disableByInternalState() && (
                  <Space size="small" align="center">
                    {groupMembershipInfo.memberDirect && (
                      <>
                        <MailingAddressSelector
                          groupId={selectedGroup.id}
                          disabled={disableByInternalState()}
                        />
                        <Divider type="vertical" style={{ height: "32px" }} />
                      </>
                    )}
                    <SubscriptionButton
                      group={selectedGroup}
                      handleRefreshMembers={handleRefreshMembers}
                    />
                  </Space>
                )}
            </Col>
          </Row>
          {groupHasManager && (
            <Alert
              type={managedButMembershipModifiable ? "info" : "error"}
              showIcon
              icon={
                <ExclamationCircleOutlined
                  style={{ marginRight: 16 }}
                  aria-hidden="true"
                />
              }
              message={getManagerMessage()}
              style={{ marginTop: 16 }}
              role="alert"
            />
          )}
          <Tabs
            items={getPanes()}
            activeKey={activeKey}
            onChange={handleTabChange}
          />
        </>
      )}
    </>
  );
};

GroupDetailsTabs.propTypes = {
  loading: PropTypes.bool,
  selectedGroup: PropTypes.object,
  isAdmin: PropTypes.bool,
  isExternalUser: PropTypes.bool,
  identity: PropTypes.object,
  groupMemberships: PropTypes.array,
  groupMembershipDirect: PropTypes.array,
  selectGroupById: PropTypes.func.isRequired,
  getGroupsIOwn: PropTypes.func.isRequired,
};

export default connect(
  (state) => ({
    loading: isApiCallLoading(state, types.GET_GROUP_BY_ID),
    selectedGroup: state.groups.selectedGroup,
    isAdmin: userTokenHasRole(state, adminRoleIdentifier),
    isExternalUser: !userTokenHasGroupUserRole(state, groupsUserRoleIdentifier),
    identity: state.identity,
    groupMemberships: state.groupMembership.recursive,
    groupMembershipDirect: state.groupMembership.direct,
  }),
  {
    selectGroupById,
    getGroupsIOwn,
  }
)(GroupDetailsTabs);
